import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  History, Loader2, TrendingUp, Calendar, Target, Trash2,
  ChevronRight, ChevronDown, RefreshCw, FileText, Clock, Eye,
  RotateCcw, CalendarPlus
} from "lucide-react";
import PlatformIcon from '../components/PlatformIcon';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StrategieHistoryPage = () => {
  const navigate = useNavigate();
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [expandedPost, setExpandedPost] = useState(null);
  const [importing, setImporting] = useState(false);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/strategy/history?limit=50`);
      setStrategies(res.data.strategies || []);
    } catch (error) {
      toast.error("Fehler beim Laden der Strategie-Historie");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleLoadDetail = async (strategyId) => {
    if (selectedStrategy?.id === strategyId) {
      setSelectedStrategy(null);
      return;
    }
    setLoadingDetail(true);
    try {
      const res = await axios.get(`${API}/strategy/${strategyId}`);
      setSelectedStrategy(res.data.strategy);
      setExpandedPost(null);
    } catch (error) {
      toast.error("Fehler beim Laden der Strategie");
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleDelete = async (strategyId, e) => {
    e.stopPropagation();
    if (!window.confirm("Strategie wirklich löschen?")) return;
    setDeleting(strategyId);
    try {
      await axios.delete(`${API}/strategy/${strategyId}`);
      toast.success("Strategie gelöscht");
      if (selectedStrategy?.id === strategyId) setSelectedStrategy(null);
      fetchHistory();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    } finally {
      setDeleting(null);
    }
  };

  const handleReuseStrategy = (strategy) => {
    const meta = strategy.meta || {};
    navigate("/strategie", {
      state: {
        prefill: {
          zeitraum: meta.zeitraum,
          hauptziel: meta.hauptziel,
          hauptthema: meta.hauptthema,
          zielgruppe: meta.zielgruppe,
          plattformen: meta.plattformen,
          tonalitaet: meta.tonalitaet,
          postFrequenz: meta.post_frequenz,
          kampagne: meta.kampagne,
        }
      }
    });
  };

  const handleImportToMonthlyPlan = async (strategyId) => {
    setImporting(true);
    try {
      const res = await axios.post(`${API}/strategy/import-to-monthly-plan`, {
        strategy_id: strategyId,
      });
      const count = res.data.imported_count ?? res.data.total_posts ?? "?";
      toast.success(`${count} Posts in den Monatsplan importiert`);
    } catch (error) {
      const msg = error.response?.data?.detail || "Import fehlgeschlagen";
      toast.error(msg);
    } finally {
      setImporting(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "–";
    return new Date(iso).toLocaleDateString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit"
    });
  };

  const getPlatformColor = (platform) => {
    switch (platform) {
      case "instagram": return "bg-pink-100 text-pink-700";
      case "facebook": return "bg-blue-100 text-blue-700";
      case "linkedin": return "bg-sky-100 text-sky-700";
      case "tiktok": return "bg-stone-900 text-white";
      case "google_business": return "bg-green-100 text-green-700";
      default: return "bg-stone-100 text-stone-600";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-fox-fire" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-stone-900 flex items-center gap-3">
            <History className="w-8 h-8 text-fox-fire" />
            Strategie-Historie
          </h1>
          <p className="text-stone-500 mt-1">
            {strategies.length} gespeicherte Strategie{strategies.length !== 1 ? "n" : ""}
          </p>
        </div>
        <button
          onClick={fetchHistory}
          className="btn-secondary flex items-center gap-2"
        >
          <RefreshCw size={16} />
          Aktualisieren
        </button>
      </div>

      {strategies.length === 0 ? (
        <div className="bg-white rounded-xl border border-stone-200 p-16 text-center">
          <History className="w-16 h-16 mx-auto mb-4 text-stone-300" />
          <h3 className="font-heading text-xl font-bold text-stone-700 mb-2">
            Noch keine Strategien gespeichert
          </h3>
          <p className="text-stone-500">
            Generiere deine erste Strategie im{" "}
            <a href="/strategie" className="text-fox-fire hover:underline font-medium">
              Strategie-Experten
            </a>
            .
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Strategy List */}
          <div className="space-y-3">
            {strategies.map((s) => {
              const meta = s.meta || {};
              const isSelected = selectedStrategy?.id === s.id;
              return (
                <div
                  key={s.id}
                  onClick={() => handleLoadDetail(s.id)}
                  className={`bg-white rounded-xl border p-4 cursor-pointer transition-all hover:shadow-md ${
                    isSelected
                      ? "border-fox-fire ring-2 ring-fox-fire/20"
                      : "border-stone-200"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      {/* Title */}
                      <div className="flex items-center gap-2 mb-1">
                        <TrendingUp size={16} className="text-fox-fire flex-shrink-0" />
                        <span className="font-heading font-bold text-stone-900 truncate">
                          {meta.hauptthema || "Strategie"}
                        </span>
                      </div>

                      {/* Meta row */}
                      <div className="flex flex-wrap items-center gap-2 text-xs text-stone-500 mb-2">
                        <span className="flex items-center gap-1">
                          <Clock size={12} />
                          {formatDate(s.created_at)}
                        </span>
                        {meta.zeitraum && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">
                            {meta.zeitraum}
                          </span>
                        )}
                      </div>

                      {/* Goal & Target */}
                      {meta.hauptziel && (
                        <div className="flex items-center gap-1 text-xs text-stone-600 mb-2">
                          <Target size={12} className="text-fox-fire" />
                          <span>{meta.hauptziel}</span>
                        </div>
                      )}

                      {/* Platforms */}
                      {meta.plattformen?.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {meta.plattformen.map((p) => (
                            <span
                              key={p}
                              className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getPlatformColor(p)}`}
                            >
                              <PlatformIcon platform={p} size={11} />
                              {p}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {loadingDetail && isSelected ? (
                        <Loader2 size={16} className="animate-spin text-fox-fire" />
                      ) : (
                        <Eye size={16} className={isSelected ? "text-fox-fire" : "text-stone-400"} />
                      )}
                      <button
                        onClick={(e) => handleDelete(s.id, e)}
                        disabled={deleting === s.id}
                        className="p-1.5 hover:bg-red-50 rounded-lg text-stone-400 hover:text-red-500 transition-colors ml-1"
                      >
                        {deleting === s.id ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Trash2 size={14} />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Overview preview */}
                  {s.overview && (
                    <p className="mt-2 text-xs text-stone-500 line-clamp-2 border-t border-stone-100 pt-2">
                      {s.overview}
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Detail Panel */}
          <div className="lg:sticky lg:top-[132px] self-start">
            {selectedStrategy ? (
              <div className="bg-white rounded-xl border border-stone-200 p-6 space-y-5">
                <div>
                  <h2 className="font-heading text-xl font-bold text-stone-900 mb-1">
                    {selectedStrategy.meta?.hauptthema || "Strategie-Detail"}
                  </h2>
                  <p className="text-sm text-stone-500 mb-3">
                    Erstellt: {formatDate(selectedStrategy.created_at)}
                  </p>
                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handleReuseStrategy(selectedStrategy)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-fox-fire text-white rounded-lg hover:bg-fox-fire/90 transition-colors font-medium"
                    >
                      <RotateCcw size={14} />
                      Erneut verwenden
                    </button>
                    <button
                      onClick={() => handleImportToMonthlyPlan(selectedStrategy.id)}
                      disabled={importing}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-stone-100 text-stone-700 rounded-lg hover:bg-stone-200 transition-colors font-medium disabled:opacity-50"
                    >
                      {importing ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <CalendarPlus size={14} />
                      )}
                      In Monatsplan
                    </button>
                  </div>
                </div>

                {/* Overview */}
                {selectedStrategy.overview && (
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex items-center gap-2 mb-2 text-amber-800 font-medium text-sm">
                      <FileText size={14} />
                      Übersicht
                    </div>
                    <p className="text-sm text-stone-700">{selectedStrategy.overview}</p>
                  </div>
                )}

                {/* Meta details */}
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: "Zeitraum", value: selectedStrategy.meta?.zeitraum },
                    { label: "Zielgruppe", value: selectedStrategy.meta?.zielgruppe },
                    { label: "Tonalität", value: selectedStrategy.meta?.tonalitaet },
                    { label: "Frequenz", value: selectedStrategy.meta?.post_frequenz },
                    { label: "Ziel", value: selectedStrategy.meta?.hauptziel },
                    { label: "Kampagne", value: selectedStrategy.meta?.kampagne || "–" },
                  ].map(({ label, value }) => value && (
                    <div key={label} className="bg-stone-50 rounded-lg p-3">
                      <div className="text-xs text-stone-500 mb-0.5">{label}</div>
                      <div className="text-sm font-medium text-stone-800">{value}</div>
                    </div>
                  ))}
                </div>

                {/* Posts */}
                {selectedStrategy.posts?.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-heading font-bold text-stone-800">
                        Posts ({selectedStrategy.posts.length})
                      </h3>
                    </div>
                    <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                      {selectedStrategy.posts.map((post, i) => (
                        <div
                          key={i}
                          className="border border-stone-200 rounded-lg overflow-hidden"
                        >
                          <button
                            onClick={() => setExpandedPost(expandedPost === i ? null : i)}
                            className="w-full flex items-center justify-between p-3 hover:bg-stone-50 transition-colors text-left"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <span className={`flex-shrink-0 flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${getPlatformColor(post.platform)}`}>
                                <PlatformIcon platform={post.platform} size={11} />
                                {post.platform}
                              </span>
                              <span className="flex items-center gap-1 text-xs text-stone-500 flex-shrink-0">
                                <Calendar size={11} />
                                {post.date}
                              </span>
                              <span className="text-sm text-stone-700 truncate">{post.hook}</span>
                            </div>
                            {expandedPost === i ? (
                              <ChevronDown size={14} className="text-stone-400 flex-shrink-0" />
                            ) : (
                              <ChevronRight size={14} className="text-stone-400 flex-shrink-0" />
                            )}
                          </button>

                          {expandedPost === i && (
                            <div className="px-3 pb-3 border-t border-stone-100 bg-stone-50 space-y-2 text-sm">
                              {post.content_type && (
                                <div>
                                  <span className="text-stone-500 text-xs">Format: </span>
                                  <span className="text-stone-700 capitalize">{post.content_type}</span>
                                </div>
                              )}
                              {post.hook && (
                                <div>
                                  <div className="text-xs font-medium text-stone-500 mb-1">Hook</div>
                                  <p className="text-stone-800">{post.hook}</p>
                                </div>
                              )}
                              {post.body_outline && (
                                <div>
                                  <div className="text-xs font-medium text-stone-500 mb-1">Body</div>
                                  <p className="text-stone-700">{post.body_outline}</p>
                                </div>
                              )}
                              {post.suggested_cta && (
                                <div>
                                  <div className="text-xs font-medium text-stone-500 mb-1">CTA</div>
                                  <p className="text-stone-700">{post.suggested_cta}</p>
                                </div>
                              )}
                              {post.hashtags?.length > 0 && (
                                <div className="flex flex-wrap gap-1 pt-1">
                                  {post.hashtags.map((tag, j) => (
                                    <span key={j} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-stone-200 p-12 text-center text-stone-400">
                <Eye className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p className="text-sm">Strategie auswählen, um Details anzuzeigen</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StrategieHistoryPage;
